<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-2xl font-bold text-gray-900">Products</h1>
      <p class="mt-1 text-gray-600">
        AI-powered tools for eLearning professionals. Choose a product to get started.
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#32cd32]"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <p class="text-red-600">{{ error }}</p>
      <button @click="fetchProducts" class="mt-2 text-sm text-red-700 underline hover:no-underline">
        Try again
      </button>
    </div>

    <!-- Products Grid -->
    <div v-else>
      <!-- Category Groups -->
      <div v-for="category in categories" :key="category.name" class="mb-10">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ category.label }}</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <a
            v-for="product in category.products"
            :key="product.id"
            :href="product.href"
            class="group bg-white rounded-xl border border-slate-200 p-6 hover:border-[#32cd32]/40 hover:shadow-lg transition-all duration-200"
          >
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-slate-100 group-hover:bg-[rgba(57,255,20,0.1)] transition-colors">
                <component
                  :is="getIcon(product.id)"
                  class="w-6 h-6 text-slate-500 group-hover:text-[#32cd32] transition-colors"
                  weight="fill"
                />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <h3 class="text-base font-semibold text-gray-900 group-hover:text-[#32cd32] transition-colors">
                    {{ product.name }}
                  </h3>
                  <span
                    v-if="product.isComingSoon"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                  >
                    Coming Soon
                  </span>
                </div>
                <p class="mt-1 text-sm text-gray-500 line-clamp-2">
                  {{ product.description }}
                </p>
                <div v-if="product.sparkCost" class="mt-3 flex items-center text-sm text-gray-400">
                  <PhSparkle class="w-4 h-4 text-yellow-500 mr-1" weight="fill" />
                  <span v-if="product.sparkCostType === 'flat'">{{ product.sparkCost }} sparks per use</span>
                  <span v-else>{{ product.sparkCost }}</span>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="products.length === 0" class="text-center py-12">
        <PhPackage class="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p class="text-gray-500">No products available.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, type Component } from 'vue';
import {
  PhSparkle,
  PhPackage,
  PhFilmSlate,
  PhMusicNotes,
  PhFileArrowDown,
  PhTranslate,
  PhPencilSimpleLine,
  PhArrowsLeftRight,
  PhMegaphone,
  PhRobot,
  PhFlask,
  PhChats,
  PhSquaresFour,
} from '@phosphor-icons/vue';
import { getAuthFromCookie } from '../../../auth/utilities/cookieAuth';

interface Product {
  id: string;
  name: string;
  description: string;
  longDescription: string;
  icon: string;
  sparkCost: number | string;
  sparkCostType: string;
  category: string;
  href: string;
  isActive: boolean;
  isComingSoon: boolean;
}

const products = ref<Product[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

const iconMap: Record<string, Component> = {
  vidsabio: PhFilmSlate,
  media: PhMusicNotes,
  extractor: PhFileArrowDown,
  'course-translator': PhTranslate,
  'course-editor': PhPencilSimpleLine,
  storyboard2rise: PhArrowsLeftRight,
  coursesignal: PhMegaphone,
  ai_tutor: PhRobot,
  virtual_labs: PhFlask,
  language_partner: PhChats,
};

function getIcon(slug: string): Component {
  return iconMap[slug] || PhSquaresFour;
}

const categories = computed(() => {
  const grouped: Record<string, Product[]> = {};

  for (const product of products.value) {
    // Skip the dashboard product - users are already here
    if (product.id === 'dashboard') continue;

    const cat = (product.category || 'Other').toLowerCase();
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(product);
  }

  // Define display order
  const order = ['build', 'transform', 'engage', 'manage', 'education', 'science', 'language', 'other'];

  return order
    .filter(cat => grouped[cat]?.length)
    .map(cat => ({
      name: cat,
      label: cat.charAt(0).toUpperCase() + cat.slice(1),
      products: grouped[cat],
    }));
});

async function fetchProducts() {
  try {
    loading.value = true;
    error.value = null;

    const auth = getAuthFromCookie();
    const token = auth.access_token || '';

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const response = await fetch(`${apiUrl}/products`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch products');
    }

    const data = await response.json();
    products.value = data.data || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'An error occurred';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  fetchProducts();
});
</script>
